/**
 * Engagement AI Service
 * Uses Gemini 3 Flash for intelligent engagement/project management features
 */

import { GoogleGenAI } from "@google/genai";
import { getAILanguage, getLanguageConfig, type SupportedLanguage } from '../utils/aiLanguageHelper';
import {
  Engagement,
  EngagementType,
  EngagementTypeDetection,
  EffortEstimate,
  TeamSuggestion,
  EngagementRisk,
  MilestoneSuggestion,
  TaskSuggestion,
  EngagementExtractedData,
  ENGAGEMENT_TYPE_CONFIG,
  TeamRole
} from '../types/engagement';
import { TeamMemberProfile } from '../types/teamSkills';
import { Task } from '../types';
import { engagementsService } from './engagementsService';
import { utilizationService } from './utilizationService';

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY || '';

let genAI: GoogleGenAI | null = null;

function getClient(): GoogleGenAI {
  if (!genAI && GEMINI_API_KEY) {
    genAI = new GoogleGenAI({ apiKey: GEMINI_API_KEY });
  }
  if (!genAI) {
    throw new Error('Gemini API key not configured');
  }
  return genAI;
}

/** True when `VITE_GEMINI_API_KEY` is set (client-side Gemini calls possible). */
export function isGeminiConfigured(): boolean {
  return Boolean(GEMINI_API_KEY.trim());
}

// Model to use - Gemini 3 Flash for speed and cost-effectiveness
const MODEL_NAME = "gemini-3-flash-preview";

export const engagementAIService = {
  // ============================================================================
  // SMART TYPE DETECTION
  // ============================================================================

  /**
   * Detect engagement type from natural language description
   */
  async detectEngagementType(description: string): Promise<EngagementTypeDetection> {
    const client = getClient();
    const language = getAILanguage();

    const typeDescriptions = Object.entries(ENGAGEMENT_TYPE_CONFIG)
      .map(([type, config]) => `- ${type}: ${config.description[language]} (typical: ${(config.typicalDuration?.[language] ?? 'ongoing')})`)
      .join('\n');

    const prompt = `Analyze this engagement description and determine the best matching type.

DESCRIPTION:
"${description}"

AVAILABLE TYPES:
${typeDescriptions}

Respond in JSON format:
{
  "suggestedType": "project" | "managed_service" | "body_leasing" | "prestudy" | "workshop" | "hackathon" | "poc" | "pilot" | "audit" | "maintenance",
  "confidence": 0.0-1.0,
  "reasoning": "Brief explanation in ${language === 'de' ? 'German' : 'English'}",
  "alternativeTypes": [
    { "type": "...", "confidence": 0.0-1.0 }
  ]
}

Consider keywords like:
- "Website", "Redesign", "Entwicklung" → project
- "Wartung", "Support", "laufend" → managed_service or maintenance
- "Machbarkeit", "testen", "POC" → poc
- "Pilot", "Testphase" → pilot
- "Workshop", "1-2 Tage", "intensiv" → workshop
- "Hackathon", "Innovation" → hackathon
- "Audit", "Prüfung", "Analyse" → audit
- "FTE", "beim Kunden", "Entwickler" → body_leasing
- "Vorstudie", "Analyse", "Bewertung" → prestudy`;

    try {
      const response = await client.models.generateContent({
        model: MODEL_NAME,
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          temperature: 0.3,
          maxOutputTokens: 1024
        }
      });

      const text = response.text || '';
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]) as EngagementTypeDetection;
      }

      // Default fallback
      return {
        suggestedType: 'project',
        confidence: 0.5,
        reasoning: 'Could not determine type with high confidence'
      };
    } catch (error) {
      console.error('Error detecting engagement type:', error);
      return {
        suggestedType: 'project',
        confidence: 0.3,
        reasoning: 'Error during analysis'
      };
    }
  },

  // ============================================================================
  // EFFORT ESTIMATION
  // ============================================================================

  /**
   * Estimate effort based on description and similar past engagements
   */
  async estimateEffort(
    orgId: string,
    description: string,
    engagementType: EngagementType
  ): Promise<EffortEstimate> {
    const client = getClient();
    const language = getAILanguage();

    // Get completed engagements for comparison
    const completedEngagements = await engagementsService.getEngagements(orgId, {
      statuses: ['completed'],
      engagementTypes: [engagementType]
    });

    // Build context from similar engagements
    const similarContext = completedEngagements.slice(0, 5).map(e => ({
      name: e.name,
      description: e.description || '',
      actualHours: e.budget?.actualHours || 0,
      actualBudget: e.budget?.actualExpenses || e.budget?.totalBudget || 0,
      durationDays: e.endDate && e.startDate 
        ? Math.ceil((new Date(e.endDate).getTime() - new Date(e.startDate).getTime()) / (1000 * 60 * 60 * 24))
        : 0
    }));

    const prompt = `Estimate effort for this engagement based on the description and similar past engagements.

NEW ENGAGEMENT:
Type: ${engagementType}
Description: "${description}"

SIMILAR COMPLETED ENGAGEMENTS (from this organization):
${similarContext.length > 0 
  ? similarContext.map(e => `- "${e.name}": ${e.actualHours}h, €${e.actualBudget}, ${e.durationDays} days`).join('\n')
  : 'No comparable engagements found.'}

Typical duration for ${engagementType}: ${(ENGAGEMENT_TYPE_CONFIG[engagementType].typicalDuration?.en ?? 'varies')}

Respond in JSON format:
{
  "estimatedHours": { "min": number, "expected": number, "max": number },
  "estimatedBudget": { "min": number, "expected": number, "max": number },
  "estimatedDuration": { "min": number, "expected": number, "max": number },
  "confidence": "low" | "medium" | "high",
  "basedOnSimilarEngagements": ${similarContext.length},
  "comparableEngagements": [
    { "id": "...", "name": "...", "actualHours": number, "actualBudget": number, "duration": number }
  ]
}

Consider:
- Project complexity indicators in the description
- Industry standards for this type of work
- Similar engagements as reference points
- Add 20-30% buffer for contingencies`;

    try {
      const response = await client.models.generateContent({
        model: MODEL_NAME,
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          temperature: 0.4,
          maxOutputTokens: 1024
        }
      });

      const text = response.text || '';
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const result = JSON.parse(jsonMatch[0]) as EffortEstimate;
        // Add IDs from actual engagements
        result.comparableEngagements = completedEngagements.slice(0, 3).map(e => ({
          id: e.id,
          name: e.name,
          actualHours: e.budget?.actualHours || 0,
          actualBudget: e.budget?.actualExpenses || 0,
          duration: e.endDate && e.startDate 
            ? Math.ceil((new Date(e.endDate).getTime() - new Date(e.startDate).getTime()) / (1000 * 60 * 60 * 24))
            : 0
        }));
        return result;
      }

      // Default fallback
      return {
        estimatedHours: { min: 80, expected: 120, max: 200 },
        estimatedBudget: { min: 10000, expected: 15000, max: 25000 },
        estimatedDuration: { min: 20, expected: 30, max: 60 },
        confidence: 'low',
        basedOnSimilarEngagements: 0,
        comparableEngagements: []
      };
    } catch (error) {
      console.error('Error estimating effort:', error);
      throw error;
    }
  },

  // ============================================================================
  // TEAM SUGGESTIONS
  // ============================================================================

  /**
   * Suggest optimal team based on requirements and availability
   */
  async suggestTeam(
    orgId: string,
    description: string,
    startDate: Date,
    endDate?: Date,
    requiredCapacity: number = 100,
    preferredSkills?: string[]
  ): Promise<TeamSuggestion[]> {
    const client = getClient();
    const language = getAILanguage();

    // Get current utilization
    const utilization = await utilizationService.calculateTeamUtilization(orgId, {
      start: startDate,
      end: endDate || new Date(startDate.getTime() + 90 * 24 * 60 * 60 * 1000) // 90 days default
    });

    // Filter to members with available capacity
    const availableMembers = utilization.filter(u => u.availableCapacity >= 20);

    if (availableMembers.length === 0) {
      return [];
    }

    const membersContext = availableMembers.map(m => ({
      userId: m.userId,
      userName: m.userName,
      availableCapacity: m.availableCapacity,
      currentEngagements: m.engagements.map(e => e.engagementName)
    }));

    const prompt = `Suggest the best team for this engagement based on availability.

ENGAGEMENT:
Description: "${description}"
Required capacity: ${requiredCapacity}%
Preferred skills: ${preferredSkills?.join(', ') || 'Not specified'}
Duration: ${startDate.toISOString()} to ${endDate?.toISOString() || 'TBD'}

AVAILABLE TEAM MEMBERS:
${membersContext.map(m => `- ${m.userName}: ${m.availableCapacity}% available, currently on: ${m.currentEngagements.join(', ') || 'none'}`).join('\n')}

Respond in JSON format (array of suggestions):
[
  {
    "userId": "...",
    "userName": "...",
    "matchScore": 0-100,
    "availableCapacity": number,
    "relevantSkills": ["..."],
    "currentEngagements": ["..."],
    "recommendation": "Brief reason in ${language === 'de' ? 'German' : 'English'}",
    "suggestedRole": "lead" | "member" | "consultant" | "support",
    "suggestedAllocation": number
  }
]

Consider:
- Match capacity to need (don't over-allocate)
- Suggest one lead and members
- Consider current workload
- Recommend realistic allocations`;

    try {
      const response = await client.models.generateContent({
        model: MODEL_NAME,
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          temperature: 0.5,
          maxOutputTokens: 2048
        }
      });

      const text = response.text || '';
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const suggestions = JSON.parse(jsonMatch[0]) as TeamSuggestion[];
        // Merge with actual utilization data
        return suggestions.map(s => {
          const actualMember = availableMembers.find(m => m.userId === s.userId);
          return {
            ...s,
            avatarUrl: actualMember?.avatarUrl,
            availableCapacity: actualMember?.availableCapacity || s.availableCapacity,
            currentEngagements: actualMember?.engagements.map(e => e.engagementName) || s.currentEngagements
          };
        });
      }

      return [];
    } catch (error) {
      console.error('Error suggesting team:', error);
      return [];
    }
  },

  // ============================================================================
  // RISK ANALYSIS
  // ============================================================================

  /**
   * Analyze engagement for potential risks
   */
  async analyzeRisks(engagement: Engagement, tasks?: Task[]): Promise<EngagementRisk[]> {
    const client = getClient();
    const language = getAILanguage();

    const taskContext = tasks?.slice(0, 20).map(t => ({
      title: t.title,
      status: t.status,
      dueDate: t.dueDate,
      priority: t.priority
    })) || [];

    const prompt = `Analyze this engagement for potential risks.

ENGAGEMENT:
Name: "${engagement.name}"
Type: ${engagement.engagementType}
Status: ${engagement.status}
Start: ${engagement.startDate}
End: ${engagement.endDate || 'Open-ended'}
Budget: ${engagement.budget?.totalBudget || 'Not set'} EUR
Used: ${engagement.budget?.actualExpenses || 0} EUR
Progress: ${engagement.progressPercentage || 0}%
Team size: ${engagement.teamAllocations.length}
Milestones: ${engagement.milestones?.length || 0} (${engagement.milestones?.filter(m => m.status === 'completed').length || 0} completed)

TASKS (${tasks?.length || 0} total):
${taskContext.map(t => `- ${t.title}: ${t.status}, due ${t.dueDate}, priority ${t.priority}`).join('\n').substring(0, 2000)}

Identify risks in JSON format:
[
  {
    "type": "budget" | "deadline" | "scope" | "resource" | "quality" | "sla",
    "severity": "low" | "medium" | "high" | "critical",
    "title": "Brief title in ${language === 'de' ? 'German' : 'English'}",
    "description": "Detailed description",
    "recommendation": "What to do about it",
    "affectedMilestones": ["milestone names if applicable"]
  }
]

Check for:
- Budget burn rate vs progress (over/underspending)
- Deadline risks (milestones overdue or at risk)
- Resource allocation (overloaded team members)
- Scope creep indicators
- Quality concerns (too many open tasks)
- SLA risks (for managed services)`;

    try {
      const response = await client.models.generateContent({
        model: MODEL_NAME,
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          temperature: 0.3,
          maxOutputTokens: 2048
        }
      });

      const text = response.text || '';
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]) as EngagementRisk[];
      }

      return [];
    } catch (error) {
      console.error('Error analyzing risks:', error);
      return [];
    }
  },

  // ============================================================================
  // MILESTONE SUGGESTIONS
  // ============================================================================

  /**
   * Suggest milestones for an engagement
   */
  async suggestMilestones(
    engagementType: EngagementType,
    description: string,
    industry?: string
  ): Promise<MilestoneSuggestion[]> {
    const client = getClient();
    const language = getAILanguage();

    const prompt = `Suggest appropriate milestones for this engagement.

TYPE: ${engagementType}
DESCRIPTION: "${description}"
INDUSTRY: ${industry || 'Not specified'}
TYPICAL DURATION: ${(ENGAGEMENT_TYPE_CONFIG[engagementType].typicalDuration?.en ?? 'varies')}

Respond in JSON format:
[
  {
    "name": "Milestone name in ${language === 'de' ? 'German' : 'English'}",
    "description": "Brief description",
    "suggestedDuration": number (days from previous milestone),
    "deliverables": ["list of deliverables"],
    "order": number (1, 2, 3...)
  }
]

For ${engagementType}, suggest ${
  engagementType === 'project' ? '4-6' :
  engagementType === 'poc' ? '2-3' :
  engagementType === 'pilot' ? '3-4' :
  '2-3'
} milestones.

Common patterns:
- Project: Kickoff → Design → Development → Testing → Launch
- POC: Analysis → Prototype → Evaluation
- Pilot: Setup → Execution → Review`;

    try {
      const response = await client.models.generateContent({
        model: MODEL_NAME,
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          temperature: 0.6,
          maxOutputTokens: 2048
        }
      });

      const text = response.text || '';
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]) as MilestoneSuggestion[];
      }

      return [];
    } catch (error) {
      console.error('Error suggesting milestones:', error);
      return [];
    }
  },

  // ============================================================================
  // TASK GENERATION
  // ============================================================================

  /**
   * Generate tasks from milestone description
   */
  async generateTasksFromMilestone(
    milestoneName: string,
    milestoneDescription: string,
    engagementContext: string
  ): Promise<TaskSuggestion[]> {
    const client = getClient();
    const language = getAILanguage();

    const prompt = `Generate specific tasks for this milestone.

MILESTONE: "${milestoneName}"
DESCRIPTION: "${milestoneDescription}"
ENGAGEMENT CONTEXT: "${engagementContext}"

Respond in JSON format:
[
  {
    "title": "Task title in ${language === 'de' ? 'German' : 'English'}",
    "description": "Brief description",
    "estimatedHours": number,
    "priority": "low" | "medium" | "high",
    "suggestedAssigneeSkills": ["skills needed"]
  }
]

Generate 3-7 specific, actionable tasks that would need to be completed for this milestone.`;

    try {
      const response = await client.models.generateContent({
        model: MODEL_NAME,
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          temperature: 0.6,
          maxOutputTokens: 2048
        }
      });

      const text = response.text || '';
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]) as TaskSuggestion[];
      }

      return [];
    } catch (error) {
      console.error('Error generating tasks:', error);
      return [];
    }
  },

  // ============================================================================
  // DOCUMENT ANALYSIS
  // ============================================================================

  /**
   * Extract engagement data from uploaded document
   */
  async extractFromDocument(
    documentText: string,
    documentType: string
  ): Promise<EngagementExtractedData> {
    const client = getClient();
    const language = getAILanguage();

    const prompt = `Extract engagement/project information from this ${documentType} document.

DOCUMENT CONTENT:
${documentText.substring(0, 30000)}

Extract in JSON format:
{
  "extractedName": "Project/Engagement name",
  "extractedBudget": number or null,
  "extractedStartDate": "YYYY-MM-DD" or null,
  "extractedEndDate": "YYYY-MM-DD" or null,
  "extractedEngagementType": "project" | "managed_service" | "poc" | ... or null,
  "extractedMilestones": ["milestone 1", "milestone 2", ...] or [],
  "extractedDeliverables": ["deliverable 1", ...] or [],
  "extractedSLAs": ["SLA 1: 99.9% uptime", ...] or [],
  "confidence": "high" | "medium" | "low"
}

Look for:
- Project name in title or header
- Budget amounts (in EUR, USD, etc.)
- Start and end dates
- Milestones or phases
- Deliverables or scope items
- SLA definitions (for service contracts)`;

    try {
      const response = await client.models.generateContent({
        model: MODEL_NAME,
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          temperature: 0.2,
          maxOutputTokens: 2048
        }
      });

      const text = response.text || '';
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]) as EngagementExtractedData;
      }

      return { confidence: 'low' };
    } catch (error) {
      console.error('Error extracting from document:', error);
      return { confidence: 'low' };
    }
  },

  // ============================================================================
  // STATUS REPORTS
  // ============================================================================

  /**
   * Generate status report for an engagement
   */
  async generateStatusReport(
    engagement: Engagement,
    tasks: Task[],
    period: 'weekly' | 'monthly' = 'weekly'
  ): Promise<{
    summary: string;
    progressPercentage: number;
    completedTasks: number;
    openTasks: number;
    budgetStatus: string;
    risks: EngagementRisk[];
    nextSteps: string[];
    highlights: string[];
  }> {
    const client = getClient();
    const language = getAILanguage();

    const completedTasks = tasks.filter(t => t.status === 'done').length;
    const openTasks = tasks.filter(t => t.status !== 'done').length;

    const prompt = `Generate a ${period} status report for this engagement.

ENGAGEMENT:
Name: "${engagement.name}"
Type: ${engagement.engagementType}
Progress: ${engagement.progressPercentage || 0}%
Budget total: ${engagement.budget?.totalBudget || 0} EUR
Budget used: ${engagement.budget?.actualExpenses || 0} EUR
Tasks completed: ${completedTasks}
Tasks open: ${openTasks}

MILESTONES:
${engagement.milestones?.map(m => `- ${m.name}: ${m.status}`).join('\n') || 'None'}

Generate report in JSON format (in ${language === 'de' ? 'German' : 'English'}):
{
  "summary": "2-3 sentence executive summary",
  "progressPercentage": number,
  "completedTasks": ${completedTasks},
  "openTasks": ${openTasks},
  "budgetStatus": "On track / Over budget / Under budget with explanation",
  "risks": [/* EngagementRisk objects */],
  "nextSteps": ["Next step 1", "Next step 2", ...],
  "highlights": ["Highlight 1", "Highlight 2", ...]
}`;

    try {
      const response = await client.models.generateContent({
        model: MODEL_NAME,
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          temperature: 0.4,
          maxOutputTokens: 2048
        }
      });

      const text = response.text || '';
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }

      // Default fallback
      return {
        summary: 'Report generation failed',
        progressPercentage: engagement.progressPercentage || 0,
        completedTasks,
        openTasks,
        budgetStatus: 'Unknown',
        risks: [],
        nextSteps: [],
        highlights: []
      };
    } catch (error) {
      console.error('Error generating status report:', error);
      throw error;
    }
  },

  // ============================================================================
  // SIMILAR ENGAGEMENTS
  // ============================================================================

  /**
   * Find similar past engagements for reference
   */
  async findSimilarEngagements(
    orgId: string,
    description: string,
    engagementType?: EngagementType
  ): Promise<{ engagement: Engagement; similarityScore: number; insights: string }[]> {
    const client = getClient();
    const language = getAILanguage();

    const completedEngagements = await engagementsService.getEngagements(orgId, {
      statuses: ['completed']
    });

    if (completedEngagements.length === 0) {
      return [];
    }

    const engagementsContext = completedEngagements.slice(0, 20).map(e => ({
      id: e.id,
      name: e.name,
      type: e.engagementType,
      description: e.description || '',
      customerName: e.customer?.name || e.customerNameManual || 'Internal',
      duration: e.endDate && e.startDate 
        ? Math.ceil((new Date(e.endDate).getTime() - new Date(e.startDate).getTime()) / (1000 * 60 * 60 * 24))
        : null,
      budget: e.budget?.totalBudget
    }));

    const prompt = `Find the most similar past engagements to this new one.

NEW ENGAGEMENT:
Description: "${description}"
Type: ${engagementType || 'Not specified'}

PAST ENGAGEMENTS:
${engagementsContext.map((e, i) => `${i + 1}. "${e.name}" (${e.type}): ${e.description?.substring(0, 100)}...`).join('\n')}

Respond in JSON format:
[
  {
    "engagementIndex": number (1-based index from list above),
    "similarityScore": 0.0-1.0,
    "insights": "What can be learned from this engagement in ${language === 'de' ? 'German' : 'English'}"
  }
]

Return top 3 most similar engagements.`;

    try {
      const response = await client.models.generateContent({
        model: MODEL_NAME,
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          temperature: 0.3,
          maxOutputTokens: 1024
        }
      });

      const text = response.text || '';
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const matches = JSON.parse(jsonMatch[0]) as { engagementIndex: number; similarityScore: number; insights: string }[];
        return matches.map(m => ({
          engagement: completedEngagements[m.engagementIndex - 1],
          similarityScore: m.similarityScore,
          insights: m.insights
        })).filter(m => m.engagement);
      }

      return [];
    } catch (error) {
      console.error('Error finding similar engagements:', error);
      return [];
    }
  }
};
